import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  fetchUsers,
  fetchInvitations,
  inviteUser,
  updateUserRole,
  updateUserStatus,
  fetchPermissionCatalog,
  updateUserPermissions,
  readApiErrorMessage,
} from "../../services/apiClient";
import { useAppUser } from "../../auth/AppUserProvider";
import { Role, ROLE_LABELS, ROLE_OPTIONS, UserStatus } from "../../auth/permissions";
import ConfirmDialog from "../../components/ConfirmDialog";
import PermissionEditor from "./PermissionEditor";
import "./AdminUsers.css";

/**
 * Admin → Users.
 *
 * Each body cell carries a `data-label` matching its column heading. It is
 * inert on a desktop; below 760px AdminUsers.css turns each row into a card
 * and renders that label beside the value, so no column is lost to a sideways
 * scroll on a phone.
 *
 * Reachable only by administrators: the route is wrapped in
 * `RequireAuth permission="users:read"`, and every call below is refused with a
 * 403 by the backend for anyone else. The client-side check only decides
 * whether to render the page, never whether an action is allowed.
 *
 * There is deliberately no subscription column here. Users, Subscribers and
 * Free Trials are three separate records: a row in this table is a OMJ VISION
 * account and its role, and nothing else. Subscription money and its state
 * live on the Subscribers page, which is the only place that reads or writes
 * them. Nothing about the backend's subscription handling changed when this
 * column was removed — only what this table shows.
 *
 * The backend also enforces the two lockout guards — an administrator cannot
 * change their own role or status, and the last active administrator cannot be
 * demoted or disabled. The controls below are disabled for the acting admin's
 * own row so the first is visible rather than only discoverable by error; the
 * second is only knowable server-side, so it surfaces as the API's message.
 */
function AdminUsers() {
  const { appUser } = useAppUser();

  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  // The permission editor. The catalog is fetched once from the backend
  // rather than duplicated here, so the toggles can only ever offer
  // permissions the server actually enforces.
  const [catalog, setCatalog] = useState(null);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [editingUser, setEditingUser] = useState(null);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);
  const [permissionError, setPermissionError] = useState(null);

  const [filters, setFilters] = useState({ role: "", status: "", search: "" });

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = {};
      if (filters.role) params.role = filters.role;
      if (filters.status) params.status = filters.status;
      if (filters.search.trim()) params.search = filters.search.trim();

      const data = await fetchUsers(params);
      setUsers(data.users);
      setTotal(data.total);
    } catch (loadError) {
      setError(readApiErrorMessage(loadError, "Could not load users."));
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  const loadInvitations = useCallback(async () => {
    try {
      const data = await fetchInvitations();
      setInvitations(data.invitations || []);
    } catch {
      // A failed invitation list must not blank the user table, which is the
      // page's actual job. It simply stays empty.
      setInvitations([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);

  async function runAction(userId, action, successMessage) {
    setBusyId(userId);
    setError(null);
    setNotice(null);

    try {
      await action();
      setNotice(successMessage);
      await load();
      await loadInvitations();
    } catch (actionError) {
      // Includes the backend's lockout guards, e.g. "This is the last active
      // administrator; promote another admin first."
      setError(readApiErrorMessage(actionError, "The change could not be saved."));
    } finally {
      setBusyId(null);
    }
  }

  /** Routes a role/status change through the confirmation dialog. */
  function requestAction(request) {
    setPendingAction(request);
  }

  function confirmPendingAction() {
    const request = pendingAction;
    setPendingAction(null);

    if (request) {
      runAction(request.userId, request.run, request.successMessage);
    }
  }

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchPermissionCatalog();
        if (cancelled || !data || !Array.isArray(data.catalog)) return;
        setCatalog(data.catalog);
        setRoleDefaults(
          Object.fromEntries(
            (data.roles || []).map((entry) => [entry.role, entry.permissions])
          )
        );
      } catch {
        // Not fatal. The table is the page; the editor is an action on it,
        // and it simply stays unavailable until the catalog can be read.
        if (!cancelled) setCatalog(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  async function savePermissions(overrides) {
    if (!editingUser) return;
    setIsSavingPermissions(true);
    setPermissionError(null);
    try {
      const { user } = await updateUserPermissions(editingUser.id, overrides);
      setUsers((current) =>
        current.map((row) => (row.id === user.id ? user : row))
      );
      setEditingUser(null);
      setNotice(`Permissions updated for ${user.email}.`);
    } catch (saveError) {
      setPermissionError(
        readApiErrorMessage(saveError, "Could not update permissions.")
      );
    } finally {
      setIsSavingPermissions(false);
    }
  }

  return (
    <main className="admin-page">
      <section className="admin-workspace">
        <header className="admin-topbar">
          <div>
            <span className="admin-kicker">Administration</span>
            <strong>Users</strong>
          </div>
          <span className="admin-count">
            {total} {total === 1 ? "user" : "users"}
          </span>
        </header>

        <div className="admin-content">
          <nav className="admin-tabs" aria-label="Administration sections">
            <Link className="is-active" to="/admin/users">Users</Link>
            <Link to="/admin/subscribers">Subscribers</Link>
            <Link to="/admin/free-trials">Free Trials</Link>
          </nav>
          <AddUserPanel
            onDone={(message) => {
              setNotice(message);
              setError(null);
              load();
              loadInvitations();
            }}
            onError={setError}
          />

          {notice ? <p className="admin-banner is-ok">{notice}</p> : null}
          {error ? <p className="admin-banner is-error">{error}</p> : null}

          <PendingInvitations invitations={invitations} />

          <div className="admin-card">
            <div className="admin-filters">
              <input
                type="search"
                value={filters.search}
                placeholder="Search name or email"
                aria-label="Search users"
                onChange={(event) =>
                  setFilters((current) => ({ ...current, search: event.target.value }))
                }
              />
              <select
                value={filters.role}
                aria-label="Filter by role"
                onChange={(event) =>
                  setFilters((current) => ({ ...current, role: event.target.value }))
                }
              >
                <option value="">All roles</option>
                {ROLE_OPTIONS.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
              <select
                value={filters.status}
                aria-label="Filter by status"
                onChange={(event) =>
                  setFilters((current) => ({ ...current, status: event.target.value }))
                }
              >
                <option value="">All statuses</option>
                <option value={UserStatus.ACTIVE}>Active</option>
                <option value={UserStatus.DISABLED}>Disabled</option>
              </select>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Account</th>
                    <th>Notifications</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="admin-empty">
                        Loading…
                      </td>
                    </tr>
                  ) : users.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="admin-empty">
                        No users match these filters.
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <UserRow
                        key={user.id}
                        user={user}
                        isSelf={appUser && user.id === appUser.id}
                        isBusy={busyId === user.id}
                        canEditPermissions={Boolean(catalog)}
                        onEditPermissions={() => {
                          setPermissionError(null);
                          setEditingUser(user);
                        }}
                        onRoleChange={(role) =>
                          requestAction({
                            userId: user.id,
                            title: "Change role",
                            body: `Change ${user.email} from ${ROLE_LABELS[user.role]} to ${ROLE_LABELS[role]}? This takes effect on their next request.`,
                            confirmLabel: `Make ${ROLE_LABELS[role]}`,
                            tone: role === Role.ADMIN ? "danger" : "normal",
                            run: () => updateUserRole(user.id, role),
                            successMessage: `${user.email} is now ${ROLE_LABELS[role]}.`,
                          })
                        }
                        onStatusToggle={(status) =>
                          requestAction({
                            userId: user.id,
                            title: status === UserStatus.ACTIVE ? "Enable account" : "Disable account",
                            body:
                              status === UserStatus.ACTIVE
                                ? `Re-enable ${user.email}? They will be able to sign in and use OMJ VISION again.`
                                : `Disable ${user.email}? They keep their Clerk login, but every OMJ VISION request — including GoldBot telemetry — is refused until they are re-enabled.`,
                            confirmLabel: status === UserStatus.ACTIVE ? "Enable" : "Disable",
                            tone: status === UserStatus.ACTIVE ? "normal" : "danger",
                            run: () => updateUserStatus(user.id, status),
                            successMessage:
                              status === UserStatus.ACTIVE
                                ? `${user.email} is enabled.`
                                : `${user.email} is disabled.`,
                          })
                        }
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <ConfirmDialog
        request={pendingAction}
        onConfirm={confirmPendingAction}
        onCancel={() => setPendingAction(null)}
      />

      {editingUser && catalog ? (
        <PermissionEditor
          user={editingUser}
          catalog={catalog}
          roleDefaults={roleDefaults}
          isSaving={isSavingPermissions}
          error={permissionError}
          onSave={savePermissions}
          onClose={() => {
            setEditingUser(null);
            setPermissionError(null);
          }}
        />
      ) : null}
    </main>
  );
}

function UserRow({
  user,
  isSelf,
  isBusy,
  canEditPermissions,
  onEditPermissions,
  onRoleChange,
  onStatusToggle,
}) {
  const name = [user.first_name, user.last_name].filter(Boolean).join(" ");
  const isDisabled = user.status !== UserStatus.ACTIVE;
  // How far this person departs from their role, shown on the button so an
  // exception is visible from the table rather than only once opened.
  const overrideCount = Object.keys(user.permission_overrides || {}).length;

  return (
    <tr className={isDisabled ? "is-disabled" : undefined}>
      <td data-label="User">
        <div className="admin-user">
          <strong>{name || "—"}</strong>
          <span>{user.email}</span>
          {user.clerk_deleted ? (
            <span className="admin-tag">Deleted in Clerk</span>
          ) : null}
        </div>
      </td>

      <td data-label="Role">
        <select
          value={user.role}
          aria-label={`Role for ${user.email}`}
          disabled={isSelf || isBusy}
          onChange={(event) => onRoleChange(event.target.value)}
        >
          {ROLE_OPTIONS.map((role) => (
            <option key={role} value={role}>
              {ROLE_LABELS[role]}
            </option>
          ))}
        </select>
        {isSelf ? <span className="admin-hint">You — cannot change your own role</span> : null}
      </td>

      <td data-label="Account">
        <span className={`admin-badge is-${isDisabled ? "off" : "on"}`}>
          {isDisabled ? "Disabled" : "Active"}
        </span>
      </td>

      <td data-label="Notifications">
        {user.role === Role.MEMBER ? (
          <span className={`admin-badge is-${user.notification_access ? "on" : "off"}`}>
            {user.notification_access ? "On" : "Off"}
          </span>
        ) : (
          <span className="admin-hint">Not applicable</span>
        )}
      </td>

      <td data-label="Created">
        <span className="admin-sub">
          {user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}
        </span>
      </td>

      <td data-label="Actions">
        <div className="admin-actions">
          <button
            type="button"
            className="admin-btn"
            disabled={isSelf || isBusy || !canEditPermissions}
            onClick={onEditPermissions}
          >
            Permissions
            {overrideCount > 0 ? (
              <span className="admin-pill">{overrideCount}</span>
            ) : null}
          </button>
          <button
            type="button"
            className="admin-btn"
            disabled={isSelf || isBusy}
            onClick={() =>
              onStatusToggle(isDisabled ? UserStatus.ACTIVE : UserStatus.DISABLED)
            }
          >
            {isBusy ? "Working…" : isDisabled ? "Enable" : "Disable"}
          </button>
        </div>
      </td>
    </tr>
  );
}

/** Invitations that have been sent but not yet accepted. */
function PendingInvitations({ invitations }) {
  if (!invitations || invitations.length === 0) {
    return null;
  }

  return (
    <div className="admin-card">
      <h2>Pending invitations</h2>
      <p className="admin-hint admin-hint--block">
        Clerk accepted these invitation requests and handles email delivery.
        The OMJ VISION account and reserved role are applied when they accept.
      </p>

      <ul className="admin-invites">
        {invitations.map((invitation) => (
          <li key={invitation.email}>
            <strong>{invitation.email}</strong>
            {[invitation.first_name, invitation.last_name].filter(Boolean).length ? (
              <span className="admin-hint">
                {[invitation.first_name, invitation.last_name].filter(Boolean).join(" ")}
              </span>
            ) : null}
            <span className="admin-tag">{ROLE_LABELS[invitation.role] || invitation.role}</span>
            <span className="admin-hint">
              {invitation.created_at
                ? `requested ${new Date(invitation.created_at).toLocaleDateString()}`
                : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AddUserPanel({ onDone, onError }) {
  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: Role.MEMBER,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [confirmRequest, setConfirmRequest] = useState(null);

  function update(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function save() {
    setIsSaving(true);
    onError(null);

    try {
      await inviteUser(form);
      onDone(
        `Invitation created for ${form.email}. Clerk accepted the email delivery request.`
      );
      setForm({ email: "", first_name: "", last_name: "", role: Role.MEMBER });
    } catch (error) {
      onError(readApiErrorMessage(error, "The invitation could not be created."));
    } finally {
      setIsSaving(false);
    }
  }

  function submit(event) {
    event.preventDefault();
    setConfirmRequest({
      title: "Send invitation",
      body: `Ask Clerk to email a secure invitation to ${form.email}? They will join as ${ROLE_LABELS[form.role]} when they accept.`,
      confirmLabel: "Send invitation",
      tone: form.role === Role.ADMIN ? "danger" : "normal",
    });
  }

  return (
    <>
      <form className="admin-card admin-add" onSubmit={submit}>
        <h2>Invite a user</h2>
        <p className="admin-hint admin-hint--block">
          Clerk sends the secure account-setup link. OMJ VISION stores the
          selected role until acceptance; it never creates passwords or
          authentication tokens.
        </p>

        <div className="admin-add__grid">
          <label>
            <span>Email</span>
            <input
              type="email"
              required
              value={form.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </label>
          <label>
            <span>Role</span>
            <select value={form.role} onChange={(event) => update("role", event.target.value)}>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABELS[role]}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>First name</span>
            <input
              type="text"
              required
              value={form.first_name}
              onChange={(event) => update("first_name", event.target.value)}
            />
          </label>
          <label>
            <span>Last name</span>
            <input
              type="text"
              required
              value={form.last_name}
              onChange={(event) => update("last_name", event.target.value)}
            />
          </label>
        </div>

        <button type="submit" className="admin-btn admin-btn--primary" disabled={isSaving}>
          {isSaving ? "Working…" : "Send invitation"}
        </button>
      </form>

      <ConfirmDialog
        request={confirmRequest}
        onConfirm={() => {
          setConfirmRequest(null);
          save();
        }}
        onCancel={() => setConfirmRequest(null)}
      />
    </>
  );
}

export default AdminUsers;
